import * as React from 'react';
import { type MatchDevice, useMatchDevice } from '@avensia/excite';
import { type Decorators } from '@avensia/excite-contentful/Document';
import { styled, type StyledDecorator } from '@glitz/react';
import { type PropsWithChildren } from 'react';
import { AdSize, useAdSize } from 'webapp/components/ad/Ad';
import { isSvenssons } from 'webapp/theme';

export const decorators: Decorators = {
  span: styled<PropsWithChildren<Excite.Contentful.ParagraphApiModel>>(props => {
    const device = useMatchDevice();
    const adSize = useAdSize();
    const { style, ...restAttributes } = props.attributes ?? {};
    const { fontSize, ...restStyles } = (style as React.CSSProperties) ?? {};
    return (
      <styled.Span {...restAttributes} style={restStyles} css={fontsizeDecorator(fontSize as string, adSize, device)}>
        {props.children}
      </styled.Span>
    );
  }),
};

const mobileFontSize: { [key: string]: string } = {
  //vw is based on 375px viewport width
  '12px': '3.2vw', // 12px
  '14px': '3.2vw', // 12px
  '16px': '3.733vw', // 14px
  '18px': '4.267vw', // 16px
  '20px': '4.8vw', // 18px
  '30px': '5.867vw', // 22px
  '42px': '8vw', // 30px
  '70px': '8vw', // 30px
};

//Svenssons mobile font size
const silMobileFontSize: { [key: string]: string } = {
  //vw is based on 375px viewport width
  '12px': '3.2vw', // 12px
  '14px': '3.2vw', // 12px
  '16px': '3.733vw', // 14px
  '18px': '4.267vw', // 16px
  '24px': '4.8vw', // 18px
  '32px': '5.867vw', // 22px
  '40px': '6.5vw', // 24px
  '56px': '7.5vw', // 28px
  '72px': '8.5vw', // 32px
};

const tabletFontSize: { [key: string]: string } = {
  //vw is based on 769px viewport width
  '12px': '1.560vw', // 12px
  '14px': '1.560vw', // 12px
  '16px': '1.821vw', // 14px
  '18px': '2.081vw', // 16px
  '20px': '2.341vw', // 18px
  '30px': '2.861vw', // 22px
  '42px': '3.901vw', // 30px
  '70px': '3.901vw', // 30px
};

//Svenssons tablet font size
const silTabletFontSize: { [key: string]: string } = {
  //vw is based on 769px viewport width
  '12px': '1.560vw', // 12px
  '14px': '1.560vw', // 12px
  '16px': '1.821vw', // 14px
  '18px': '2.081vw', // 16px
  '24px': '2.341vw', // 18px
  '32px': '2.861vw', // 22px
  '40px': '3.301vw', // 25px
  '56px': '3.801vw', // 30px
  '72px': '4.301vw', // 32px
};

const desktopFontSize: { [key: string]: string } = {
  //vw is based on 1600px viewport width
  '12px': 'min(0.75vw, 12px)',
  '14px': 'min(0.875vw, 14px)',
  '16px': 'min(1vw, 16px)',
  '18px': 'min(1.125vw, 18px)',
  '20px': 'min(1.250vw, 20px)',
  '30px': 'min(1.875vw, 30px)',
  '42px': 'min(2.625vw, 42px)',
  '70px': 'min(4.375vw, 70px)',
};

//Svenssons desktop font size
const silDesktopFontSize: { [key: string]: string } = {
  //vw is based on 1600px viewport width
  '12px': 'min(0.75vw, 12px)',
  '14px': 'min(0.875vw, 14px)',
  '16px': 'min(1vw, 16px)',
  '18px': 'min(1.125vw, 18px)',
  '24px': 'min(1.250vw, 20px)',
  '32px': 'min(1.875vw, 32px)',
  '40px': 'min(2.625vw, 40px)',
  '56px': 'min(4.375vw, 56px)',
  '72px': 'min(4.375vw, 72px)',
};

const smallAdFontSize: { [key: string]: string } = {
  '12px': '12px',
  '14px': '14px',
  '16px': '14px',
  '18px': '14px',
  '20px': '16px',
  '30px': '18px',
  '42px': '20px',
  '70px': '30px',
};

const mediumAdFontSize: { [key: string]: string } = {
  '12px': '12px',
  '14px': '14px',
  '16px': '16px',
  '18px': '16px',
  '20px': '18px',
  '30px': '20px',
  '42px': '30px',
  '70px': '42px',
};

function mobileStyle(fontSize: string) {
  return styled({ fontSize: isSvenssons ? silMobileFontSize[fontSize] : mobileFontSize[fontSize] });
}

function tabletStyle(fontSize: string, adSize?: AdSize) {
  if (adSize) {
    return styled({ fontSize: mediumAdFontSize[fontSize] });
  }
  return styled({ fontSize: isSvenssons ? silTabletFontSize[fontSize] : tabletFontSize[fontSize] });
}

function desktopStyle(fontSize: string, adSize?: AdSize) {
  switch (adSize) {
    case AdSize.Large:
      return styled({ fontSize: fontSize });
    case AdSize.Medium:
      return styled({ fontSize: mediumAdFontSize[fontSize] });
    case AdSize.Small:
      return styled({ fontSize: smallAdFontSize[fontSize] });
  }
  return styled({ fontSize: isSvenssons ? silDesktopFontSize[fontSize] : desktopFontSize[fontSize] });
}

function fontsizeDecorator(fontSize: string, adSize: AdSize | undefined, device: MatchDevice): StyledDecorator {
  if (device.isDesktop || device.isLaptop) {
    return desktopStyle(fontSize, adSize);
  } else if (device.isTablet) {
    return tabletStyle(fontSize, adSize);
  }
  return mobileStyle(fontSize);
}
